import React from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { checklists } from '../data/checklists';
import { CheckCircle2Icon, ClockIcon, TrendingUpIcon, ArrowLeftIcon } from 'lucide-react';
interface ChecklistSummaryProps {
  checklistId: string;
  completedSteps: string[];
  duration: number;
  onBack: () => void;
}
export function ChecklistSummary({
  checklistId,
  completedSteps,
  duration,
  onBack
}: ChecklistSummaryProps) {
  const {
    colors
  } = useTheme();
  const checklist = checklists.find(c => c.id === checklistId);
  if (!checklist) {
    return <div className="p-4" data-id="element-105">
        <p data-id="element-106">Checklist not found.</p>
        <button onClick={onBack} className="mt-4 text-sm" style={{
        color: colors.accent
      }} data-id="element-107">
          Go Back
        </button>
      </div>;
  }
  const completionRate = Math.round(completedSteps.length / checklist.steps.length * 100);
  const complianceLevel = completionRate === 100 ? 'Excellent' : completionRate >= 80 ? 'Good' : 'Needs Improvement';
  const complianceColor = completionRate === 100 ? colors.success : completionRate >= 80 ? colors.warning : colors.error;
  const completionTime = new Date().toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit'
  });
  return <div className="min-h-screen -m-4" style={{
    backgroundColor: colors.background
  }} data-id="element-108">
      {/* Header */}
      <div className="sticky top-0 z-10 p-4 border-b" style={{
      backgroundColor: colors.cardSurface,
      borderColor: colors.divider
    }} data-id="element-109">
        <button onClick={onBack} className="flex items-center gap-2" style={{
        color: colors.accent
      }} data-id="element-110">
          <ArrowLeftIcon size={20} data-id="element-111" />
          <span data-id="element-112">Back to Checklists</span>
        </button>
      </div>
      <div className="p-4 space-y-6" data-id="element-113">
        {/* Success Message */}
        <div className="text-center py-8" data-id="element-114">
          <div className="w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-4" style={{
          backgroundColor: colors.success + '20'
        }} data-id="element-115">
            <CheckCircle2Icon size={48} style={{
            color: colors.success
          }} data-id="element-116" />
          </div>
          <h1 className="text-2xl font-bold mb-2" style={{
          color: colors.textPrimary
        }} data-id="element-117">
            Checklist Submitted Successfully!
          </h1>
          <p style={{
          color: colors.textSecondary
        }} data-id="element-118">
            Your compliance data has been recorded
          </p>
        </div>
        {/* Summary Cards */}
        <div className="space-y-4" data-id="element-119">
          {/* Checklist Info */}
          <div className="p-6 rounded-xl" style={{
          backgroundColor: colors.cardSurface
        }} data-id="element-120">
            <h2 className="text-lg font-bold mb-4" style={{
            color: colors.textPrimary
          }} data-id="element-121">
              {checklist.title}
            </h2>
            <div className="space-y-3" data-id="element-122">
              <div className="flex items-center justify-between" data-id="element-123">
                <span style={{
                color: colors.textSecondary
              }} data-id="element-124">
                  Completion Time
                </span>
                <div className="flex items-center gap-2" data-id="element-125">
                  <ClockIcon size={16} style={{
                  color: colors.textSecondary
                }} data-id="element-126" />
                  <span className="font-semibold" style={{
                  color: colors.textPrimary
                }} data-id="element-127">
                    {completionTime}
                  </span>
                </div>
              </div>
              <div className="flex items-center justify-between" data-id="element-128">
                <span style={{
                color: colors.textSecondary
              }} data-id="element-129">
                  Duration
                </span>
                <span className="font-semibold" style={{
                color: colors.textPrimary
              }} data-id="element-130">
                  {duration} {duration === 1 ? 'minute' : 'minutes'}
                </span>
              </div>
              <div className="flex items-center justify-between" data-id="element-131">
                <span style={{
                color: colors.textSecondary
              }} data-id="element-132">
                  Steps Completed
                </span>
                <span className="font-semibold" style={{
                color: colors.textPrimary
              }} data-id="element-133">
                  {completedSteps.length} / {checklist.steps.length}
                </span>
              </div>
            </div>
          </div>
          {/* Compliance Level */}
          <div className="p-6 rounded-xl" style={{
          backgroundColor: colors.cardSurface
        }} data-id="element-134">
            <div className="flex items-center gap-3 mb-4" data-id="element-135">
              <TrendingUpIcon size={24} style={{
              color: colors.textPrimary
            }} data-id="element-136" />
              <h2 className="text-lg font-bold" style={{
              color: colors.textPrimary
            }} data-id="element-137">
                Compliance Level
              </h2>
            </div>
            <div className="mb-4" data-id="element-138">
              <div className="flex items-center justify-between mb-2" data-id="element-139">
                <span style={{
                color: colors.textSecondary
              }} data-id="element-140">
                  Completion Rate
                </span>
                <span className="text-3xl font-bold" style={{
                color: complianceColor
              }} data-id="element-141">
                  {completionRate}%
                </span>
              </div>
              <div className="w-full h-3 rounded-full overflow-hidden" style={{
              backgroundColor: colors.divider
            }} data-id="element-142">
                <div className="h-full rounded-full transition-all" style={{
                width: `${completionRate}%`,
                backgroundColor: complianceColor
              }} data-id="element-143" />
              </div>
            </div>
            <div className="text-center py-3 rounded-lg font-semibold" style={{
            backgroundColor: complianceColor + '20',
            color: complianceColor
          }} data-id="element-144">
              {complianceLevel}
            </div>
          </div>
        </div>
        {/* Actions */}
        <div className="space-y-3" data-id="element-145">
          <button onClick={onBack} className="w-full p-4 rounded-xl font-semibold" style={{
          backgroundColor: colors.accent,
          color: '#FFFFFF'
        }} data-id="element-146">
            Return to Checklists
          </button>
        </div>
      </div>
    </div>;
}